import {
  CreativeImportConfirmRequest,
  CreativeImportCopyPools,
  CreativeImportEditState,
  CreativeImportJobStatus,
  CreativeImportProgress,
  CreativeImportSlideState,
  CreativeImportValidationReport,
  FBCallToAction,
} from '@marketing-milk/interfaces'

export type CreativeImportStep = 'upload' | 'review' | 'options' | 'import' | 'summary'

/**
 * One media file of a row, ready to render in Review. A row has one of these for
 * single image/video and one per slide/asset otherwise, so preview state can no
 * longer be a single URL per row.
 *
 * Video needs TWO urls: the blob object URL the `<video>` plays, and a separate
 * canvas-derived still for the rail thumbnail and the preview poster.
 */
export interface RowMediaPreview {
  /** 0-based, matching the manifest's slide/asset order. */
  ordinal: number
  /** How to RENDER it (a .gif is an image here even though it uploads as video). */
  kind: 'image' | 'video'
  /** Blob object URL — must be revoked. */
  sourceUrl: string
  /** Canvas-derived first frame for video. A data URL, so nothing to revoke. */
  posterUrl?: string
  width?: number
  height?: number
}

/** Every object URL held by a row's previews, for revocation. */
export function previewObjectUrls(previews: Record<number, RowMediaPreview[]>): string[] {
  // posterUrl is deliberately excluded — it's a data URL, not an object URL.
  return Object.values(previews).flatMap(row => row.map(preview => preview.sourceUrl))
}

export type SlideUIState = Required<CreativeImportSlideState>
export type CopyPoolsUIState = Required<CreativeImportCopyPools>

/** Per-row editable fields at Review. All stored as strings (CTA is a string enum). */
export interface RowUIState {
  selected: boolean
  editedName: string
  editedPrimaryText: string
  editedHeadline: string
  editedDescription: string
  editedCallToAction: string
  editedDestinationURL: string
  /** S7d: the ad's "why". Free text, never blocks. */
  editedJustification: string
  /** S7b: present only for carousel rows, ordered by slide. */
  editedSlides?: SlideUIState[]
  /** S7c: present only for dynamic rows. */
  editedCopyPools?: CopyPoolsUIState
}

/** Keys of RowUIState the Review cells can edit — payload of the `editField` action. */
export type EditableRowField =
  | 'editedName'
  | 'editedPrimaryText'
  | 'editedHeadline'
  | 'editedDescription'
  | 'editedCallToAction'
  | 'editedDestinationURL'
  | 'editedJustification'

/** Keys of SlideUIState the carousel slide tabs can edit — payload of `editSlideField`. */
export type EditableSlideField = 'headline' | 'description' | 'destinationUrl'

/** Which pool a `editCopyPool` action targets. */
export type CopyPoolName = keyof CopyPoolsUIState

export interface CreativeImportState {
  step: CreativeImportStep
  file: File | null
  validating: boolean
  report: CreativeImportValidationReport | null
  /** Persisted import-job id from validate; null until a clean report comes back. */
  creativeImportID: number | null
  /** Per-row UI state keyed by rowNumber. */
  rows: Record<number, RowUIState>
  /**
   * Ordered media previews per rowNumber. Revoked on reset/unmount — there are
   * more URLs per row now (a carousel has one per slide), same leak surface.
   */
  previews: Record<number, RowMediaPreview[]>
  /** Optional sprint to attach imported drafts to — existing or created inline (Options step). */
  sprintID: number | null
  /**
   * S6b: ensure the chosen sprint has a client packet at Confirm. Defaults on —
   * the packet is the expected outcome of the flow, not an extra — and is
   * ignored without a sprint.
   */
  createPacket: boolean
  confirming: boolean
  /** True once the backend accepted the confirm (incl. a 409 "already confirmed"). Gates progress polling. */
  confirmed: boolean
  confirmError: string | null
  progress: CreativeImportProgress | null
  error: string | null
}

export type CreativeImportAction =
  | { type: 'validateStart'; file: File }
  | { type: 'validateSuccess'; report: CreativeImportValidationReport }
  | { type: 'validateError'; message: string }
  | { type: 'thumbnailsLoaded'; thumbnails: Record<number, RowMediaPreview[]> }
  | { type: 'toggleRow'; rowNumber: number }
  | { type: 'setAllRows'; selected: boolean }
  | { type: 'editField'; rowNumber: number; field: EditableRowField; value: string }
  | {
      type: 'editSlideField'
      rowNumber: number
      ordinal: number
      field: EditableSlideField
      value: string
    }
  | { type: 'editCopyPool'; rowNumber: number; pool: CopyPoolName; values: string[] }
  | { type: 'goToOptions' }
  | { type: 'goToImport' }
  | { type: 'backToReview' }
  | { type: 'backToOptions' }
  | { type: 'setSprint'; sprintID: number | null }
  | { type: 'setCreatePacket'; createPacket: boolean }
  | { type: 'confirmStart' }
  | { type: 'confirmSuccess' }
  | { type: 'confirmError'; message: string }
  | { type: 'progressUpdated'; progress: CreativeImportProgress }
  | { type: 'hydrateFromProgress'; progress: CreativeImportProgress }
  | { type: 'hydratePreConfirm'; editState: CreativeImportEditState }
  | { type: 'reset' }

export const initialCreativeImportState: CreativeImportState = {
  step: 'upload',
  file: null,
  validating: false,
  report: null,
  creativeImportID: null,
  rows: {},
  previews: {},
  sprintID: null,
  createPacket: true,
  confirming: false,
  confirmed: false,
  confirmError: null,
  progress: null,
  error: null,
}

const TERMINAL_JOB_STATUSES: CreativeImportJobStatus[] = [
  CreativeImportJobStatus.Completed,
  CreativeImportJobStatus.CompletedWithErrors,
  CreativeImportJobStatus.Failed,
  CreativeImportJobStatus.Interrupted,
]

export function isTerminalJobStatus(status: CreativeImportJobStatus): boolean {
  return TERMINAL_JOB_STATUSES.includes(status)
}

/** Selectable rows start selected (warnings included); blocked rows stay unselected. */
export function seedRowState(report: CreativeImportValidationReport): Record<number, RowUIState> {
  return Object.fromEntries(
    report.rows.map(row => [
      row.rowNumber,
      {
        selected: row.selectable,
        editedName: row.effectiveName,
        // Seeds mirror the backend's effective* columns: manifest copy (or blank),
        // CTA fallback-applied so the dropdown always shows a supported value.
        editedPrimaryText: row.effectivePrimaryText ?? '',
        editedHeadline: row.effectiveHeadline ?? '',
        editedDescription: row.effectiveDescription ?? '',
        editedCallToAction: row.effectiveCallToAction,
        editedDestinationURL: row.effectiveDestinationUrl ?? '',
        editedJustification: row.effectiveJustification ?? '',
        // Present only for the format that has them, so a non-carousel row can't
        // accidentally submit slides (and vice versa).
        editedSlides: row.effectiveSlides?.map(slide => ({
          ordinal: slide.ordinal,
          headline: slide.headline ?? '',
          description: slide.description ?? '',
          destinationUrl: slide.destinationUrl ?? '',
        })),
        editedCopyPools: row.effectiveCopyPools && {
          primaryTexts: row.effectiveCopyPools.primaryTexts ?? [],
          headlines: row.effectiveCopyPools.headlines ?? [],
          descriptions: row.effectiveCopyPools.descriptions ?? [],
          callToActions: row.effectiveCopyPools.callToActions ?? [],
        },
      },
    ])
  )
}

/**
 * CR: rebuild the Review row state when reopening an unconfirmed import. Seeds
 * from the reconstructed report (effective* already reflect autosaved copy
 * edits), then applies the persisted selection over the default.
 */
export function seedRowStateFromEditState(
  editState: CreativeImportEditState
): Record<number, RowUIState> {
  const seeded = seedRowState(editState.report)
  const selected = new Set(editState.selectedRowNumbers)
  return Object.fromEntries(
    Object.entries(seeded).map(([rowNumber, ui]) => [
      rowNumber,
      { ...ui, selected: selected.has(Number(rowNumber)) },
    ])
  )
}

/**
 * Shapes the confirm payload from the report + per-row edits: selected rows
 * only, each carrying its name + copy overrides. Pure so it's unit-testable
 * (handleConfirm just calls it).
 */
export function buildConfirmRows(
  report: CreativeImportValidationReport,
  rows: Record<number, RowUIState>
): CreativeImportConfirmRequest['rows'] {
  return report.rows
    .filter(row => rows[row.rowNumber]?.selected)
    .map(row => {
      const ui = rows[row.rowNumber]
      return {
        rowNumber: row.rowNumber,
        name: ui.editedName,
        primaryText: ui.editedPrimaryText,
        headline: ui.editedHeadline,
        description: ui.editedDescription,
        callToAction: ui.editedCallToAction as FBCallToAction,
        destinationUrl: ui.editedDestinationURL,
        justification: ui.editedJustification,
        // Sent only when the row's format actually has them — an absent field
        // leaves the persisted seed in force, so an empty array would be a
        // meaningful (and wrong) "clear all slides".
        slides: ui.editedSlides,
        copyPools: ui.editedCopyPools,
      }
    })
}

export function creativeImportReducer(
  state: CreativeImportState,
  action: CreativeImportAction
): CreativeImportState {
  switch (action.type) {
    case 'validateStart':
      return { ...initialCreativeImportState, file: action.file, validating: true }
    case 'validateSuccess':
      return {
        ...state,
        validating: false,
        report: action.report,
        creativeImportID: action.report.creativeImportID,
        rows: seedRowState(action.report),
        // Archive-level rejection keeps the user on Upload with the errors shown.
        step: action.report.archiveErrors.length ? 'upload' : 'review',
      }
    case 'validateError':
      return { ...state, validating: false, error: action.message }
    case 'thumbnailsLoaded':
      return { ...state, previews: action.thumbnails }
    case 'toggleRow': {
      const row = state.rows[action.rowNumber]
      if (!row) return state
      return {
        ...state,
        rows: { ...state.rows, [action.rowNumber]: { ...row, selected: !row.selected } },
      }
    }
    // Select-all/none over selectable rows; blocked rows never select.
    case 'setAllRows': {
      if (!state.report) return state
      const selectable = new Set(
        state.report.rows.filter(row => row.selectable).map(row => row.rowNumber)
      )
      return {
        ...state,
        rows: Object.fromEntries(
          Object.entries(state.rows).map(([rowNumber, ui]) => [
            rowNumber,
            selectable.has(Number(rowNumber)) ? { ...ui, selected: action.selected } : ui,
          ])
        ),
      }
    }
    case 'editField': {
      const row = state.rows[action.rowNumber]
      if (!row) return state
      return {
        ...state,
        rows: { ...state.rows, [action.rowNumber]: { ...row, [action.field]: action.value } },
      }
    }
    // S7b: one slide's copy cell. Ordinals come from the manifest's slide list,
    // so an out-of-range one is a bug rather than user input — ignore it.
    case 'editSlideField': {
      const row = state.rows[action.rowNumber]
      if (!row?.editedSlides?.[action.ordinal]) return state
      return {
        ...state,
        rows: {
          ...state.rows,
          [action.rowNumber]: {
            ...row,
            editedSlides: row.editedSlides.map((slide, ordinal) =>
              ordinal === action.ordinal ? { ...slide, [action.field]: action.value } : slide
            ),
          },
        },
      }
    }
    // S7c: a whole pool at once — a pool is an ordered list, and add/remove/reorder
    // are all list-level operations, so there's no per-item action to write.
    case 'editCopyPool': {
      const row = state.rows[action.rowNumber]
      if (!row?.editedCopyPools) return state
      return {
        ...state,
        rows: {
          ...state.rows,
          [action.rowNumber]: {
            ...row,
            editedCopyPools: { ...row.editedCopyPools, [action.pool]: action.values },
          },
        },
      }
    }
    case 'goToOptions':
      return { ...state, step: 'options' }
    case 'goToImport':
      return { ...state, step: 'import' }
    case 'backToReview':
      return { ...state, step: 'review', confirmError: null }
    case 'backToOptions':
      return { ...state, step: 'options', confirmError: null }
    case 'setSprint':
      return { ...state, sprintID: action.sprintID }
    case 'setCreatePacket':
      return { ...state, createPacket: action.createPacket }
    case 'confirmStart':
      return { ...state, confirming: true, confirmError: null }
    case 'confirmSuccess':
      return { ...state, confirming: false, confirmed: true }
    case 'confirmError':
      return { ...state, confirming: false, confirmError: action.message }
    case 'progressUpdated':
      return {
        ...state,
        progress: action.progress,
        // The Summary is an explicit step so closing/reopening state is testable.
        step: isTerminalJobStatus(action.progress.status) ? 'summary' : state.step,
      }
    // Reopening a confirmed job by URL: rebuild a fresh confirmed session
    // around the polled progress. Upload/review state is gone by design —
    // pre-confirm restoration is deferred recovery (plan §12). The caller
    // must not hydrate an awaiting_confirmation job (it would poll forever).
    case 'hydrateFromProgress':
      return {
        ...initialCreativeImportState,
        creativeImportID: action.progress.creativeImportID,
        confirmed: true,
        progress: action.progress,
        step: isTerminalJobStatus(action.progress.status) ? 'summary' : 'import',
      }
    // CR: reopening an unconfirmed import by URL. Rebuild the pre-confirm Review
    // state from the persisted edits. Stays unconfirmed on the Review step.
    case 'hydratePreConfirm':
      return {
        ...initialCreativeImportState,
        report: action.editState.report,
        creativeImportID: action.editState.report.creativeImportID,
        rows: seedRowStateFromEditState(action.editState),
        sprintID: action.editState.sprintID,
        createPacket: action.editState.createPacket,
        step: 'review',
      }
    case 'reset':
      return initialCreativeImportState
    default:
      return state
  }
}
